<template>
  <div
    role="button"
    class="d-block order-card"
    :class="{ 'is-closed-order': !isOpenedOrder }"
    @click="$emit('card-click', order.id)"
  >
    <div>
      <div class="order-card-header">
        <OrderLabel
          :order="order"
          :orderService="orderServiceName"
          :isShowAdditionalLabel="isShowAdditionalLabel"
        />
        <WatchLaterButton
          v-if="isWatchLaterButtonVisible"
          class="ml-auto"
          isShowButtonText
          :isRegisteredWatchLater="isRegisteredWatchLater"
          @click="$emit('change-watch-later', $event)"
        />
      </div>
      <div class="d-flex justify-content-between">
        <div
          class="order-card-detail mr-2 d-flex flex-column justify-content-between"
        >
          <div
            class="order-card-title order-card-title--break mb-1 text-size-14"
          >
            <span class="d-flex align-items-center"
              ><BeginnerMark
                v-if="order.hasSpecificRecommendationPoint"
                class="flex-shrink-0 beginner-mark"
              />{{ order.title }}</span
            >
          </div>
          <RemainingDateTime
            :expiredAt="closestOrderWorkTimeOptionVisibleTo"
            :isHiring="order.isHiring"
          />
        </div>
        <div v-if="orderImage">
          <BImgLazy :src="orderImage" alt="" class="order-card-image" />
        </div>
      </div>
      <div
        class="d-flex flex-column text-mild align-items-start text-size-17 mt-2"
      >
        <ul class="work-time-option-list">
          <li
            v-for="option in visibleWorkTimeOptions"
            :key="option.id"
            class="d-flex justify-content-between"
          >
            <span class="d-flex align-items-center">
              <FeatherIcon icon="clock" size="16" class="ml-2 mr-1" />{{
                getOrderTime(option)
              }}
            </span>
            <span
              class="d-flex align-items-center salary-per-day-with-various-allowances"
            >
              {{ getSalaryPerDayWithVariousAllowances(option) }}円</span
            >
          </li>
        </ul>
      </div>

      <div class="mt-1 d-flex">
        <div class="ml-auto hourly-wage">
          {{ hourlyWageLabel }}
        </div>
      </div>
    </div>

    <div class="office-info mt-2">
      <div class="d-flex align-items-center flex-wrap">
        <span class="mr-2 office-name">{{ order.office.name }}</span>
        <template v-if="isLogin">
          <EvaluationWithCount
            v-if="evaluation"
            :value="evaluation.evaluationScore"
            :count="evaluation.evaluationCount"
            text-size="12"
            size="14"
          />
          <span v-else class="text-mild text-size-12 wait-fetch-evaluation"
            >評価データ取得中...</span
          >
        </template>
      </div>
      <div class="d-flex justify-content-between pl-1">
        <div class="office-info-detail">
          <div class="d-flex align-items-center text-mild text-size-12">
            <FeatherIcon icon="map-pin" size="14" class="map-icon mr-1" />
            <div class="office-info-address">
              {{ order.office.address }}
            </div>
          </div>
          <div class="d-flex align-items-center text-mild text-size-12 mt-1">
            <FeatherIcon icon="navigation" size="14" class="map-icon mr-1" />
            <div class="office-info-access">
              {{ order.access }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-facing-decorator";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import EvaluationWithCount from "@/components/molecules/other/EvaluationWithCount.vue";
import OrderLabel from "@/components/molecules/orders/OrderLabel.vue";
import RemainingDateTime from "@/components/organisms/orders/RemainingDateTime.vue";
import WatchLaterButton from "@/components/atoms/button/WatchLaterButton.vue";
import { getOptimizedImageUrl } from "@/utils/image";
import { orderService } from "@/app/services/OrderService";
import { SlimOrder } from "@/types/order/Order";
import { masterStore } from "@/store/master";
import { OrderWorkTimeOption } from "@/types/order/OrderWorkTimeOption";
import { parseISO } from "@/utils/date";
import { formatNumber } from "@/utils/number";
import { Evaluation } from "@/types/office/Evaluation";

@Component({
  components: {
    FeatherIcon,
    EvaluationWithCount,
    RemainingDateTime,
    OrderLabel,
    WatchLaterButton
  }
})
export default class OrderCardSimple extends Vue {
  @Prop({ type: Object, required: true })
  order!: SlimOrder;
  @Prop({ type: Boolean, required: true })
  isLogin!: boolean;
  @Prop({ type: Boolean, required: true })
  isRegisteredWatchLater!: boolean;
  @Prop({ type: Object, default: null })
  evaluation!: Evaluation | null;
  @Prop({ type: Boolean, default: false })
  isShowAdditionalLabel!: boolean;

  orderDateListScrollLeft = 0;
  isOrderDateListScrollStart = true;
  isOrderDateListScrollEnd = false;

  get orderServiceName() {
    return masterStore.getServiceNameById(this.order.service);
  }

  get orderImage() {
    return getOptimizedImageUrl(this.order!.image1, 240, 240);
  }

  get isOpenedOrder() {
    return this.order.isHiring;
  }

  get variousAllowances() {
    return this.order.variousAllowances;
  }

  get hourlyWageLabel() {
    return `時給${formatNumber(this.order.hourlyWage)}円`;
  }

  get visibleWorkTimeOptions(): OrderWorkTimeOption[] {
    if (this.order.isHiring) {
      return this.notExpiredWorkTimeOptions;
    } else {
      return this.order.workTimeOptions;
    }
  }

  get notExpiredWorkTimeOptions(): OrderWorkTimeOption[] {
    return orderService.getNotExpiredWorkTimeOptions(
      this.order.workTimeOptions
    );
  }

  get closestOrderWorkTimeOptionVisibleTo(): number | null {
    const options = [...this.notExpiredWorkTimeOptions]
      .filter(option => option && option.visibleTo && option.startWorkAt)
      .sort((a, b) => {
        return parseISO(a.visibleTo) - parseISO(b.startWorkAt);
      });
    if (options.length > 0) {
      return parseISO(options[0].visibleTo);
    } else {
      return null;
    }
  }

  get isWatchLaterButtonVisible() {
    return orderService.isWatchLaterButtonVisible(this.order);
  }

  getSalaryPerDayWithVariousAllowances(option: OrderWorkTimeOption): string {
    if (!option) return "";
    return formatNumber(option.salaryPerDay + this.variousAllowances) || "";
  }
  getOrderTime(option: OrderWorkTimeOption) {
    if (!option) return "";
    return orderService.getFormattedWorkTimeRangeFromDate(
      option.startWorkAt,
      option.finishWorkAt
    );
  }
}
</script>

<style lang="scss" scoped>
.order-card {
  padding: 12px;
  overflow: hidden;
  background-color: $white;
  border: 1px solid $gray-03;
  border-radius: 4px;
}

.order-card-header {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.order-card-image {
  width: 90px;
  height: 72px;
  border-radius: 4px;
  object-fit: cover;
}

.order-card-title {
  display: -webkit-box;
  overflow: hidden;
  line-height: 18px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  &--break {
    overflow-wrap: break-word;
  }

  &--ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.order-card-detail {
  width: calc(100% - 72px);
}

.office-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  background: $gray-02;
  border-radius: 4px;
}

.map-icon {
  min-width: 14px;
  margin-top: 2px;
}

.office-name {
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
}

.is-closed-order {
  background-color: $gray-03;
}

.office-info-detail {
  width: 100%;
}

.office-info-address {
  width: 100%;
  overflow: hidden;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.office-info-access {
  width: 100%;
  line-height: 16px;
}

.order-time-list {
  display: block;
}

.work-time-option-list {
  width: 100%;

  li {
    height: 40px;
    padding: 4px 0;
    border-bottom: 1px solid #e5e5e5;
  }

  li:first-child .salary-per-day-with-various-allowances {
    color: #d23e36;
  }

  li:last-child {
    border-bottom: none;
  }

  .salary-per-day-with-various-allowances {
    font-size: 20px;
    font-weight: 700;
  }
}

.hourly-wage {
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: $mild;
}

.point {
  font-weight: 600;
}

.wait-fetch-evaluation {
  min-width: 140px;
}
</style>
